import { useState } from 'react';
import { Alert, Button, Card, Input, Radio, Select } from '@dreamtree-org/twreact-ui';
import { CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Money, PageHeader, SectionCard } from '@/components';
import { useMutation, useQuery } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import { AGES, QUESTIONS, SEGMENTS, missingAnswers } from '../lib/buyback';
import { isValidPhone, isValidPin } from '../lib/cart';
import { useStorefront } from '../StorefrontContext';
import SlotPicker, { dayLabel } from '../components/SlotPicker';

const radio = 'var(--color-primary-600)';

/**
 * WEB-11 buyback: questionnaire → provisional, NON-BINDING amount → inspection
 * appointment. The final offer is made only after a physical inspection (C-08).
 */
export default function Sell() {
  const sf = useStorefront();
  const estimate = useMutation('sfBuybackEstimate');
  const book = useMutation('sfBuybackBook');
  const slots = useQuery('sfBuybackSlots', {}, { select: (r) => r.data });
  const [a, setA] = useState({});
  const [touched, setTouched] = useState(false);
  const [est, setEst] = useState(null);
  const [appt, setAppt] = useState({ mode: 'home', pin: sf.pin || '', name: (sf.session && sf.session.name) || '', phone: (sf.session && sf.session.phone) || '', slot: null });
  const [booked, setBooked] = useState(null);
  const missing = missingAnswers(a);
  const set = (k, v) => {
    setA((x) => ({ ...x, [k]: v }));
    setEst(null);
    setBooked(null);
  };

  const getEstimate = async () => {
    setTouched(true);
    if (missing.length) return;
    try {
      const r = await estimate.run(a);
      setEst(r.data);
    } catch {
      /* shown below */
    }
  };

  const apptErr = {
    name: !appt.name.trim() ? 'Required' : '',
    phone: !isValidPhone(appt.phone) ? 'Enter a 10-digit mobile number' : '',
    pin: appt.mode === 'home' && !isValidPin(appt.pin) ? 'Enter a 6-digit PIN code' : '',
  };

  const navigate = useNavigate();
  const doBook = async () => {
    if (!sf.session) {
      navigate(`/account/login?next=${encodeURIComponent('/sell')}`);
      return;
    }
    try {
      const r = await book.run({ estimate_id: est.estimate_id, ...appt });
      setBooked(r.data);
    } catch {
      /* shown below */
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Sell or exchange your device" subtitle="Answer a few questions for a provisional value, then book a free inspection." />
      <div className="grid gap-5 lg:grid-cols-5">
        <SectionCard title="About your device" className="lg:col-span-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Select label="Type of device" options={SEGMENTS} value={a.segment} onChange={(v) => set('segment', v)} placeholder="Choose one" closeOnSelect error={touched && missing.includes('segment') ? 'Required' : undefined} />
            <Select label="Age" options={AGES} value={a.age} onChange={(v) => set('age', v)} placeholder="How old is it?" closeOnSelect error={touched && missing.includes('age') ? 'Required' : undefined} />
          </div>
          <div className="space-y-4">
            {QUESTIONS.map((qn) => (
              <fieldset key={qn.key} className="space-y-1.5">
                <legend className={`text-sm font-medium ${touched && missing.includes(qn.key) ? 'text-error-600' : ''}`}>{qn.label}{touched && missing.includes(qn.key) ? ' (required)' : ''}</legend>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {qn.options.map((o) => (
                    <div key={o.value} className="flex min-h-9 items-center">
                      <Radio name={qn.key} value={o.value} checked={a[qn.key] === o.value} onChange={() => set(qn.key, o.value)} label={o.label} radioColor={radio} />
                    </div>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
          {estimate.error ? <Alert variant="error">{estimate.error}</Alert> : null}
          <Button size="lg" loading={estimate.loading} onClick={getEstimate}>Get my estimate</Button>
        </SectionCard>

        <div className="space-y-5 lg:col-span-2">
          {!est ? (
            <Card className="space-y-2 p-5 text-sm">
              <div className="font-semibold">How it works</div>
              <ol className="list-decimal space-y-1 pl-5 opacity-80">
                <li>Answer 7 questions (about a minute).</li>
                <li>See a provisional value range. It is not an offer.</li>
                <li>Book a free inspection at home or in store.</li>
                <li>Get a final offer; take cash by UPI or exchange credit.</li>
              </ol>
            </Card>
          ) : (
            <SectionCard title="Your provisional estimate">
              <div className="text-center">
                <Money paise={est.amount_paise} className="text-3xl font-bold text-primary-700" />
                <div className="text-sm opacity-70">Likely range <Money paise={est.low_paise} /> – <Money paise={est.high_paise} /></div>
              </div>
              <Alert variant="warning" title="Non-binding">This is an estimate, not an offer. The final price is confirmed only after a physical inspection. Valid until {formatDate(est.valid_until)}.</Alert>
              {est.factors.length ? (
                <ul className="space-y-1 text-xs opacity-80">
                  {est.factors.map((f) => <li key={f.label}>{f.label}: ×{f.factor}</li>)}
                </ul>
              ) : null}
            </SectionCard>
          )}

          {est && !booked ? (
            <SectionCard title="Book a free inspection">
              <div className="flex flex-wrap gap-4">
                <Radio name="appt-mode" value="home" checked={appt.mode === 'home'} onChange={() => setAppt((x) => ({ ...x, mode: 'home' }))} label="At my home" radioColor={radio} />
                <Radio name="appt-mode" value="store" checked={appt.mode === 'store'} onChange={() => setAppt((x) => ({ ...x, mode: 'store' }))} label="At the store" radioColor={radio} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Name" value={appt.name} onChange={(e) => setAppt((x) => ({ ...x, name: e.target.value }))} error={appt.name || appt.phone ? apptErr.name || undefined : undefined} />
                <Input label="Mobile" type="tel" value={appt.phone} onChange={(e) => setAppt((x) => ({ ...x, phone: e.target.value }))} error={appt.phone ? apptErr.phone || undefined : undefined} />
                {appt.mode === 'home' ? <Input label="PIN code" type="tel" inputMode="numeric" maxLength={6} value={appt.pin} onChange={(e) => setAppt((x) => ({ ...x, pin: e.target.value.replace(/\D/g, '') }))} error={appt.pin ? apptErr.pin || undefined : undefined} /> : null}
              </div>
              <SlotPicker days={slots.data} value={appt.slot} onChange={(s) => setAppt((x) => ({ ...x, slot: s }))} />
              {book.error ? <Alert variant="error">{book.error}</Alert> : null}
              {!sf.session ? <p className="text-xs opacity-70">Booking needs a quick sign-in with your mobile number; you will answer the questions again afterwards.</p> : null}
              <Button fullWidth disabled={sf.session && (!appt.slot || Object.values(apptErr).some(Boolean))} loading={book.loading} onClick={doBook}>{sf.session ? 'Book inspection' : 'Sign in to book'}</Button>
            </SectionCard>
          ) : null}

          {booked ? (
            <Card className="space-y-2 p-5 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-success-600" aria-hidden="true" />
              <div className="font-semibold">Inspection booked · {booked.id}</div>
              <p className="text-sm opacity-80">{booked.mode === 'home' ? 'Our inspector visits you' : `Visit ${sf.brand.name}`} on {dayLabel(booked.slot.date)}, {booked.slot.label}. Keep the charger and your ID handy.</p>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
